import { useCallback, useEffect, useRef, useState } from "react";
import { clockElapsedMs, parseClockState } from "../utils/liveScoreState";

// ─── localStorage helpers ──────────────────────────────────────────────────
// Fallback only: the match clock lives on the server (clock_state, LS-B09).
// { elapsedSeconds, runningSince } — runningSince (ms) is set while the clock
// runs, so leaving with "Keep Running" and coming back resumes correctly.
const TIMER_KEY = (id) => `live_timer_${id}`;

const fmtSeconds = (total) => {
  const s = Math.max(0, Math.floor(total));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const loadPersisted = (matchId) => {
  if (!matchId) return { elapsedSeconds: 0, runningSince: null };
  try {
    const raw = localStorage.getItem(TIMER_KEY(matchId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return {
          elapsedSeconds: typeof parsed.elapsedSeconds === "number" ? parsed.elapsedSeconds : 0,
          runningSince: typeof parsed.runningSince === "number" ? parsed.runningSince : null,
        };
      }
    }
  } catch {}
  return { elapsedSeconds: 0, runningSince: null };
};

const savePersisted = (matchId, elapsedSeconds, runningSince = null) => {
  if (!matchId) return;
  try {
    localStorage.setItem(
      TIMER_KEY(matchId),
      JSON.stringify(runningSince ? { elapsedSeconds, runningSince } : { elapsedSeconds })
    );
  } catch {}
};

const clearPersisted = (matchId) => {
  if (!matchId) return;
  try { localStorage.removeItem(TIMER_KEY(matchId)); } catch {}
};

const isLive = (status) => status === "in_progress" || status === "active";

/**
 * The match clock shown on the live page.
 *
 * - The clock state is kept on the server (clock_state: elapsedMs, running,
 *   runningSince) and broadcast as 'clock_update' (LS-B09). Pass it as
 *   `serverClock`; every device — scorers and viewers — shows the same time.
 * - The scorer (`canControl`) drives it: start / timeout / resume call
 *   `onClockChange({ running, elapsedMs, period })`, which stores it.
 * - The clock keeps running while the tab is hidden and after leaving the
 *   page ("Keep Running & Leave"); only a timeout pauses it.
 * - `managesOwnClock`: basketball / football boards run their own period
 *   clock (and store it); this hook then only keeps a local elapsed display.
 */
const useLiveMatchTimer = ({
  match,
  serverClock = null,
  canControl = false,
  onClockChange = null,
  managesOwnClock = false,
} = {}) => {
  const matchId = match?.id;
  const [matchDuration, setMatchDuration] = useState("0:00");
  const [isTimeoutActive, setIsTimeoutActiveRaw] = useState(false);

  // Clock: total = base + (now - runningSince)
  const baseElapsedRef   = useRef(0);    // seconds counted before the current run
  const runningSinceRef  = useRef(null); // Date.now() when the current run began
  const tickRef          = useRef(null);
  const matchIdRef       = useRef(matchId);
  const isTimeoutRef     = useRef(false);
  const matchStatusRef   = useRef(match?.status);
  const onClockChangeRef = useRef(onClockChange);
  const canPostRef       = useRef(false);
  const usesServerRef    = useRef(false);

  matchIdRef.current = matchId;
  matchStatusRef.current = match?.status;
  onClockChangeRef.current = onClockChange;
  canPostRef.current = !!canControl && !managesOwnClock && typeof onClockChange === "function";
  usesServerRef.current = !managesOwnClock;

  const computeTotal = useCallback(() => {
    if (runningSinceRef.current === null) return baseElapsedRef.current;
    return baseElapsedRef.current + Math.floor((Date.now() - runningSinceRef.current) / 1000);
  }, []);

  const render = useCallback(() => {
    const total = computeTotal();
    setMatchDuration(isTimeoutRef.current ? `${fmtSeconds(total)} (Paused)` : fmtSeconds(total));
    return total;
  }, [computeTotal]);

  const stopTicking = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startTicking = useCallback(() => {
    if (tickRef.current) return;
    tickRef.current = setInterval(() => {
      const total = render();
      if (total % 5 === 0) savePersisted(matchIdRef.current, total, runningSinceRef.current);
    }, 1000);
  }, [render]);

  const post = useCallback((running) => {
    if (!canPostRef.current) return;
    onClockChangeRef.current({ running, elapsedMs: computeTotal() * 1000, period: 1 });
  }, [computeTotal]);

  // ── run / pause (local), optionally stored on the server ─────────────────
  const runClock = useCallback((notify = true) => {
    if (runningSinceRef.current === null) {
      runningSinceRef.current = Date.now();
      savePersisted(matchIdRef.current, baseElapsedRef.current, runningSinceRef.current);
      if (notify) post(true);
    }
    startTicking();
  }, [post, startTicking]);

  const pauseClock = useCallback((notify = true) => {
    stopTicking();
    if (runningSinceRef.current !== null) {
      baseElapsedRef.current = computeTotal();
      runningSinceRef.current = null;
      if (notify) post(false);
    }
    savePersisted(matchIdRef.current, baseElapsedRef.current, null);
  }, [computeTotal, post, stopTicking]);

  // Alias used by LiveMatch.js when a user-triggered timeout happens
  const stopTimer = useCallback(() => pauseClock(true), [pauseClock]);

  const setIsTimeoutActive = useCallback((value) => {
    const next = typeof value === "function" ? value(isTimeoutRef.current) : value;
    isTimeoutRef.current = !!next;
    setIsTimeoutActiveRaw(!!next);
  }, []);

  /** Back to 0:00 (explicit clock reset only — resetting the score keeps the clock). */
  const resetTimer = useCallback(() => {
    stopTicking();
    baseElapsedRef.current = 0;
    runningSinceRef.current = null;
    clearPersisted(matchIdRef.current);
    setMatchDuration("0:00");
    if (isLive(matchStatusRef.current) && !isTimeoutRef.current) {
      runningSinceRef.current = Date.now();
      startTicking();
      if (canPostRef.current) onClockChangeRef.current({ running: true, elapsedMs: 0, period: 1 });
    } else if (canPostRef.current) {
      onClockChangeRef.current({ running: false, elapsedMs: 0, period: 1 });
    }
  }, [startTicking, stopTicking]);

  // ── load the persisted clock when matchId first resolves ─────────────────
  useEffect(() => {
    if (!matchId) return;
    const persisted = loadPersisted(matchId);
    baseElapsedRef.current = persisted.elapsedSeconds;
    runningSinceRef.current = persisted.runningSince;
    setMatchDuration(fmtSeconds(computeTotal()));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // ── follow the server clock (all devices) ────────────────────────────────
  const serverKey = serverClock
    ? `${serverClock.running}|${serverClock.runningSince}|${serverClock.elapsedMs ?? serverClock.elapsed_ms}`
    : null;
  useEffect(() => {
    if (!usesServerRef.current) return;
    const clock = parseClockState(serverClock);
    if (!clock || !isLive(matchStatusRef.current)) return;
    const elapsedSec = Math.floor(clockElapsedMs(clock) / 1000);
    if (clock.running) {
      baseElapsedRef.current = Math.floor(clock.elapsedMs / 1000);
      runningSinceRef.current = Date.parse(clock.runningSince);
      if (isTimeoutRef.current) setIsTimeoutActive(false);
      startTicking();
    } else {
      stopTicking();
      baseElapsedRef.current = elapsedSec;
      runningSinceRef.current = null;
      // A clock paused on another device is a timeout here too
      if (!isTimeoutRef.current && elapsedSec > 0) setIsTimeoutActive(true);
    }
    savePersisted(matchIdRef.current, baseElapsedRef.current, runningSinceRef.current);
    render();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey, match?.status]);

  // ── react to match status ────────────────────────────────────────────────
  useEffect(() => {
    if (!match) return;
    const status = match.status;

    if (isLive(status)) {
      // With a server clock, its state decides (effect above); otherwise run.
      const hasServerClock = usesServerRef.current && !!parseClockState(serverClock);
      if (!hasServerClock && !isTimeoutRef.current) runClock(true);
      else if (hasServerClock && runningSinceRef.current !== null) startTicking();
    } else if (status === "completed") {
      stopTicking();
      runningSinceRef.current = null;
      const ended = match.ended_at || match.endedAt || match.completed_at || match.completedAt;
      const started = match.started_at || match.startedAt;
      if (started && ended) {
        const diff = Math.floor((new Date(ended) - new Date(started)) / 1000);
        setMatchDuration(`${fmtSeconds(diff)} (Final)`);
      } else {
        setMatchDuration("Completed");
      }
      clearPersisted(matchId);
    } else if (status === "scheduled") {
      setMatchDuration("Not started");
    } else if (status === "cancelled") {
      stopTicking();
      runningSinceRef.current = null;
      setMatchDuration("Cancelled");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.status]);

  // ── react to timeout toggle ──────────────────────────────────────────────
  const firstTimeoutRunRef = useRef(true);
  useEffect(() => {
    if (firstTimeoutRunRef.current) {
      firstTimeoutRunRef.current = false;
      return;
    }
    if (!isLive(matchStatusRef.current)) return;
    if (isTimeoutActive) {
      pauseClock(true);
      setMatchDuration((prev) => (prev.includes("(Paused)") ? prev : `${fmtSeconds(baseElapsedRef.current)} (Paused)`));
    } else {
      setMatchDuration(fmtSeconds(baseElapsedRef.current));
      runClock(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimeoutActive]);

  // ── unmount: stop ticking, keep the clock running ("Keep Running & Leave") ─
  useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    const id = matchIdRef.current;
    if (!id || !isLive(matchStatusRef.current)) return;
    const total = runningSinceRef.current === null
      ? baseElapsedRef.current
      : baseElapsedRef.current + Math.floor((Date.now() - runningSinceRef.current) / 1000);
    // Saved as elapsed-so-far plus "running since now": coming back resumes
    // from the real wall-clock time even without the server clock.
    savePersisted(id, total, runningSinceRef.current === null ? null : Date.now());
  }, []);

  return { matchDuration, isTimeoutActive, setIsTimeoutActive, stopTimer, resetTimer };
};

export default useLiveMatchTimer;
